import { useMemo } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  DuckDBStatusEnum,
  selectDuckDBStatus,
  selectScanDataTableExists,
  setColumnsExistInDbData,
} from '../../duckDB/duckDBSlice';
import useDuckQueryScanData, {
  DuckQueryReturnOneType,
} from '../../duckDB/hooks/useDuckQueryScanData';
import { ScanDataEnum } from '../scanDataDefinitions';
interface MeasureDataQueryResult {
  column_name: string;
}

export interface MeasureDataResult {
  [key: string]: boolean;
}

/**
 * Custom React hook to check if certain columns exist in a specified database table.
 *
 * @param tableName - The name of the table in the database.
 * @param duckMeasures - An array of column names to check for existence in the table.
 * @param updateReduxScanData - Whether or not to update the redux store with the result of the query.
 * @returns An object containing the result of the query and the data indicating the existence of each column.  This is sent to redux for scanData, but the value is also returned in case this function is used for another table or for other measures in the future.
 */
export const useColumnsExistInDB = (
  tableName: string,
  duckMeasures: string[],
  updateReduxScanData = true
): DuckQueryReturnOneType<MeasureDataResult> => {
  const dispatch = useAppDispatch();
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const duckDbStatus = useAppSelector(selectDuckDBStatus);

  const baseQuery = useMemo(() => {
    return `SELECT COLUMN_NAME
    FROM INFORMATION_SCHEMA.COLUMNS
    WHERE TABLE_NAME = '${tableName}'`;
  }, [tableName]);

  const result = useDuckQueryScanData<MeasureDataQueryResult>(
    baseQuery,
    !duckMeasures ||
      !scanDataTableExists ||
      duckDbStatus !== DuckDBStatusEnum.dataLoaded
  );

  const finalResult = useDeepCompareMemo(() => {
    if (!result.data) {
      return {
        ...result,
        data: null,
      };
    }

    const availableCols = result.data.map((el) => el.column_name);
    const doesMeasureExists = Object.fromEntries(
      duckMeasures.map((measure) => [
        measure,
        !!availableCols.includes(measure),
      ])
    );

    const resultFormatted = {
      ...result,
      data: doesMeasureExists,
    };

    if (updateReduxScanData) {
      dispatch(setColumnsExistInDbData(resultFormatted));
    }

    return resultFormatted;
  }, [result, duckMeasures, updateReduxScanData, dispatch]);

  return finalResult;
};

export const filterScanDataColumns = (
  cols: (string | ScanDataEnum)[],
  availableCols: MeasureDataResult
) => {
  const duckAvailableCols = Object.entries(availableCols)
    .filter(([, isAvailable]) => isAvailable)
    .map(([col]) => col);

  // get all defined columns
  const scanDataCols = Object.entries(ScanDataEnum).map(([, val]) => val);
  return cols.filter((col) => {
    // look if this col - is defined column.
    const matchedScanDataCol = scanDataCols.find((availableCol) =>
      col.includes(availableCol)
    );
    // if column is not defined - keep it because it's mostly computed col
    if (!matchedScanDataCol) return true;
    // if defined column is in the list of available - keep it
    if (duckAvailableCols.includes(matchedScanDataCol)) {
      return true;
    }
    // in other cases filter out this column
    return false;
  });
};
